/**
 * Listing domain helpers
 * Pure functions with no external dependencies.
 */

export const DEFAULT_DETAIL_SYNC_THRESHOLD_DAYS = 7;

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value._seconds === 'number') {
    const millis = value._seconds * 1000 + Math.floor((value._nanoseconds || 0) / 1e6);
    return new Date(millis);
  }
  return null;
}

function normalizeString(value) {
  return (value || '').toLowerCase();
}

export function isListingDefinitelyInCorrectCity({ details, addresses, cityName }) {
  if (details?.isInCorrectCity) return true;
  const address = addresses && addresses[0];
  if (!address || !address.city || !cityName) return false;
  return normalizeString(address.city) === normalizeString(cityName);
}

export function computeListingUrl({ url, source }) {
  if (!url) return url;
  if (source === 'vrbo') {
    return url + (url.includes('?') ? '&' : '?') + 'noDates=true';
  }
  return url;
}

export function getListingSourceInfo(source) {
  if (source === 'airbnb') {
    return {
      iconUrl: 'https://a0.muscache.com/airbnb/static/icons/android-icon-192x192-c0465f9f0380893768972a31a614b670.png',
      name: 'Airbnb'
    };
  }
  if (source === 'vrbo') {
    return {
      iconUrl: 'https://csvcus.homeaway.com/rsrcs-crs/cdn-logos/5.1.2/sitename/vrbo/web/favicon.ico',
      name: 'Vrbo'
    };
  }
  if (source === 'booking') {
    return {
      iconUrl: 'https://cf.bstatic.com/static/img/favicon/40749a316c45e239a7149b6711ea4c48d10f8d89.ico',
      name: 'Booking.com'
    };
  }
  return {
    iconUrl: null,
    name: source || 'Unknown'
  };
}

export function extractListingLatLng(details) {
  const lat = details?.latitude;
  const lng = details?.longitude;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return { lat, lng };
}

export function isListingDueForDetailImport({
  detailsImported,
  dateDetailsImported,
  thresholdDays = DEFAULT_DETAIL_SYNC_THRESHOLD_DAYS
}) {
  if (!detailsImported) return true;
  const date = toDate(dateDetailsImported);
  if (!date) return true;
  const diffMs = Date.now() - date.getTime();
  return diffMs > thresholdDays * 24 * 60 * 60 * 1000;
}

export function isListingWhitelisted({ whitelisted, whitelistExpiration, now = new Date() }) {
  if (!whitelisted) return false;
  if (!whitelistExpiration) return true;
  if (typeof whitelistExpiration.isAfter === 'function') {
    return whitelistExpiration.isAfter(now);
  }
  const expiration = toDate(whitelistExpiration);
  if (!expiration) return true;
  return expiration.getTime() > now.getTime();
}

export function hasListingDeferral({ whitelisted }) {
  return !!whitelisted;
}

export function getListingDeferralEndDate({ whitelisted, whitelistExpiration }) {
  if (!hasListingDeferral({ whitelisted }) || !whitelistExpiration) {
    return null;
  }
  return toDate(whitelistExpiration);
}

export function hasListingDeferralEnded({ whitelisted, whitelistExpiration, now = new Date() }) {
  const endDate = getListingDeferralEndDate({ whitelisted, whitelistExpiration });
  if (!endDate) {
    return false;
  }
  return endDate.getTime() <= now.getTime();
}

export function getListingDeferralStatus({ whitelisted, whitelistExpiration, now = new Date() }) {
  if (!hasListingDeferral({ whitelisted })) {
    return 'none';
  }
  return hasListingDeferralEnded({ whitelisted, whitelistExpiration, now }) ? 'ended' : 'active';
}

export function listingMatchesQuery({ query, addressFormatted, title }) {
  const q = normalizeString(query);
  const address = normalizeString(addressFormatted);
  const listingTitle = normalizeString(title);
  return address.indexOf(q) >= 0 || listingTitle.indexOf(q) >= 0;
}

export function listingIndexKeys({ addressFormatted, title, host }) {
  const address = normalizeString(addressFormatted);
  const listingTitle = normalizeString(title);
  const listingHost = normalizeString(host);
  return (address + ' ' + listingTitle + ' ' + listingHost)
    .split(/ /g)
    .map(word => word.trim())
    .filter(word => !!word);
}

export function buildListingVerificationUpdate({
  propertyId,
  property,
  currentUser,
  notes,
  now = new Date()
}) {
  return {
    verifiedPropertyId: propertyId || null,
    verifiedProperty: property || null,
    verifiedAt: now,
    verifiedBy: currentUser || null,
    verifiedNotes: notes || null
  };
}

export function buildListingWhitelistedUpdate({
  whitelisted,
  reason,
  expiration,
  currentUser,
  now = new Date()
}) {
  return {
    whitelisted: !!whitelisted,
    whitelistReason: reason,
    whitelistExpiration: expiration,
    whitelistUpdatedAt: now,
    whitelistUpdatedBy: currentUser || null
  };
}

export function buildListingActiveStatusUpdate({
  active,
  currentUser,
  now = new Date()
}) {
  return {
    status: active ? 'active' : 'inactive',
    statusOverride: true,
    statusOverriddenAt: now,
    statusOverriddenBy: currentUser || null
  };
}
/** The selected public listing photo, shared by the UI and thumbnail worker. */
export function getListingPrimaryImageUrl(data = {}) {
  const image = data.listing?.images?.[data.primaryImageIndex || 0];
  return data.primaryImageUrl || image?.url || image || null;
}
